import { Button, Modal } from "@ai-teach/ui-web";
import { useState } from "react";

import { useSelection } from "@/contexts/SelectionContext";
import type { SlideDomainEnum } from "@/generated/graphql";
import { SLIDE_DOMAINS } from "@/lib/constants/slides";
import { useSetSlideDomains } from "@/lib/hooks/slides/useSetSlideDomains";

interface SetSlideDomainModalProps {
  isOpen: boolean;
}

export function SetSlideDomainModal({ isOpen }: SetSlideDomainModalProps) {
  const { selectedSlideIds, toggleSetDomainModal, clearAllSelections } = useSelection();
  const [domains, setReports] = useState<SlideDomainEnum[]>([]);

  const { setSlideDomains, loading } = useSetSlideDomains();

  const slideCount = selectedSlideIds.length;

  const toggleDomain = (domain: SlideDomainEnum) => {
    setReports((prev) =>
      prev.includes(domain) ? prev.filter((r) => r !== domain) : [...prev, domain]
    );
  };

  const handleClose = () => {
    toggleSetDomainModal(false);
    clearAllSelections();
  };

  const handleConfirm = async () => {
    if (slideCount === 0 || domains.length === 0) return;
    try {
      await setSlideDomains(selectedSlideIds, domains);
      handleClose();
    } catch (error) {
      // Error already surfaced by the hook's toast
      console.error("Set slide domain failed:", error);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Set Domain for ${slideCount} Slide${slideCount === 1 ? "" : "s"}`}
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <label className="label flex items-center gap-1">
            <span className="text-red-500">*</span> Domain
          </label>
          <div className="flex flex-wrap gap-2">
            {SLIDE_DOMAINS.map((r) => {
              const pickOrder = domains.indexOf(r.enum) + 1;
              const isSelected = pickOrder > 0;
              return (
                <button
                  key={r.enum}
                  type="button"
                  onClick={() => toggleDomain(r.enum)}
                  className={`h-9 px-4 rounded-full border label whitespace-nowrap transition-colors flex items-center gap-2 ${
                    isSelected
                      ? "bg-accent-500 border-accent-500 text-white shadow-sm"
                      : "border-gray-200 dark:border-gray-800 text-secondary-200 hover:border-accent-500 hover:bg-accent-500/10"
                  }`}
                >
                  {isSelected && (
                    <span className="w-4 h-4 rounded-full bg-white/25 text-[10px] leading-none flex items-center justify-center">
                      {pickOrder}
                    </span>
                  )}
                  {r.label}
                </button>
              );
            })}
          </div>
          {/* Pick order is stored order, and anything that needs a single domain takes the first. */}
          <p className="text-xs text-secondary-400">
            Replaces the existing selection. Scores count in full under each domain chosen,
            independent of skill. Numbered by pick order — #1 is the one used where only one domain
            fits.
          </p>
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-gray-800">
          <Button variant="secondary" size="sm" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleConfirm}
            disabled={slideCount === 0 || domains.length === 0 || loading}
          >
            {loading ? "Updating..." : "Set Domain"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
